// Export and import of a song's count: one JSON file per song. Pure functions (no DOM, no storage), so Node tests them.
//
// Schema 5 (S9e) adds three events to the log and changes nothing else: `auto` (the automatic proposal, with its `proposal` object), `confirm` ("Yes") and `level` ("Too fast" or "Too slow": `change` is
// `halve` or `double`). A schema 4 file is read as it always was; a build from before S9e refuses a schema 5 file with "saved by a newer version". `buildAllExport` writes the other file,
// "Export all my counts": every song stored on the device, one entry each with its identity and its whole log (no audio), under its own schema number (ALL_COUNTS_SCHEMA).
//
// Schema 4 (S4c) adds two things to the event log and changes nothing else: the `drop` event (`tap`: the identity of the tap it removes) and an
// optional `tap` on `undo` (cancel the drop of that tap). A schema 3 file is read as it always was; a build from before S4c refuses a schema 4 file
// with "saved by a newer version" instead of failing on an event it does not know.
// Schema 3 writes tap times already corrected by the calibration in force when each tap was made (`offsetS` and `device` say
// which). Schema 2 wrote the raw heard time with the offset beside it; reading one takes the offset off the taps, so every tap means what it meant
// when it was made, and this device's calibration is never applied to a file's taps. Schema 2 writes: the track's identity (SHA-256 of the file bytes, name, duration), the event log (each event with
// when it was made and the tap offset in force then), the calibration used on the exporting device, and the tracker's version.
// Schema 1 (the S3c export) wrote `{ version: 1, track, events: [bare events], exportedAt }` and is still read.
// A file with a higher schema than this build knows is refused rather than half-read.
//
// Import merges by track hash. The log is append-only, so a merge appends the file's events that this device does not have, in the
// file's order, and never removes or rewrites one. Events already here are counted and reported, not dropped silently. Calibration
// in a file is information only: it belongs to the device that made it and is not applied to this one.

import type { AutoProposal, CountEvent, Word } from '@salsacount/core';

export const SCHEMA_VERSION = 5;

const KINDS = new Set(['one', 'reanchor', 'shift', 'flip', 'half', 'clave', 'reset', 'undo', 'drop', 'auto', 'confirm', 'level']);
const CLAVE = new Set(['3-2', '2-3', 'unknown']);

export interface ExportedEvent {
  readonly event: CountEvent;
  /** Wall-clock ms when it was made; undefined in a schema 1 file. */
  readonly wall?: number;
  /** The device's tap offset (seconds) in force when it was made. */
  readonly offsetS?: number;
  /** The output device it was made on. */
  readonly device?: string;
}

export interface CalibrationUsed {
  readonly offsetS: number;
  readonly device: string;
  readonly taps?: number;
  readonly madeAt?: string;
}

export interface CountExport {
  readonly app: 'salsacount';
  readonly version: number;
  readonly track: { readonly sha256: string; readonly name: string; readonly duration: number };
  /** Flat: each entry is the event's own fields plus `wall` and `offsetS`. */
  readonly events: readonly Record<string, unknown>[];
  readonly calibration: CalibrationUsed | null;
  readonly tracker: { readonly version: string };
  readonly exportedAt: string;
}

export interface ExportInput {
  readonly track: { readonly id: string; readonly name: string; readonly duration: number };
  readonly records: readonly { readonly event: CountEvent; readonly wall: number; readonly offsetS: number; readonly device?: string }[];
  readonly calibration: CalibrationUsed | null;
  readonly trackerVersion: string;
  readonly now?: Date;
}

export function buildExport(input: ExportInput): CountExport {
  return {
    app: 'salsacount',
    version: SCHEMA_VERSION,
    track: { sha256: input.track.id, name: input.track.name, duration: input.track.duration },
    events: input.records.map((r) => ({ ...r.event, wall: r.wall, offsetS: r.offsetS, ...(r.device !== undefined ? { device: r.device } : {}) })),
    calibration: input.calibration,
    tracker: { version: input.trackerVersion },
    exportedAt: (input.now ?? new Date()).toISOString(),
  };
}

export interface ParsedExport {
  readonly version: number;
  /** null when an old file did not say. */
  readonly trackId: string | null;
  readonly name: string | null;
  readonly events: readonly ExportedEvent[];
  readonly calibration: CalibrationUsed | null;
  readonly trackerVersion: string | null;
}

export type ParseResult =
  | { readonly ok: true; readonly doc: ParsedExport }
  | { readonly ok: false; readonly sentence: string };

const NOT_A_COUNT = 'That file is not a saved count. Choose a file made with Export count.';

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const finite = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

const WORDS = new Set<unknown>(['sure', 'likely', 'unsure']);

/** The proposal of an `auto` event from its JSON form, or null when a field is missing or wrong. Exported for the bench's reader (tools/bench/counts_truth.ts reads the same shape). */
export function readProposal(raw: unknown): AutoProposal | null {
  if (!isObject(raw) || !finite(raw.bpm) || !finite(raw.anchor) || !finite(raw.one) || !isObject(raw.words) || !isObject(raw.margins) || !isObject(raw.evidence) || !Array.isArray(raw.levels)) return null;
  const w = raw.words, m = raw.margins, ev = raw.evidence;
  const keys = ['tempo', 'beat', 'odd', 'bar', 'one'] as const;
  if (!keys.every((k) => WORDS.has(w[k]) && finite(m[k]))) return null;
  if (!finite(ev.chosen) || !finite(ev.other) || !finite(ev.events) || !finite(ev.boundaries) || !finite(ev.p)) return null;
  if (!raw.levels.every((l) => isObject(l) && finite(l.bpm) && finite(l.score))) return null;
  if (raw.direction !== '3-2' && raw.direction !== '2-3' && raw.direction !== 'none') return null;
  const word = (k: (typeof keys)[number]) => w[k] as Word;
  return {
    bpm: raw.bpm, anchor: raw.anchor, one: raw.one,
    words: { tempo: word('tempo'), beat: word('beat'), odd: word('odd'), bar: word('bar'), one: word('one') },
    margins: { tempo: m.tempo as number, beat: m.beat as number, odd: m.odd as number, bar: m.bar as number, one: m.one as number },
    evidence: { chosen: ev.chosen, other: ev.other, events: ev.events, boundaries: ev.boundaries, p: ev.p },
    levels: (raw.levels as { bpm: number; score: number }[]).map((l) => ({ bpm: l.bpm, score: l.score })),
    direction: raw.direction,
  };
}

/** One event from its flat JSON form, or null when it is not a valid event. Extra fields are ignored. Exported for the bench's reader of "Export all my counts" (`tools/bench/counts_truth.ts`). */
export function readEvent(raw: unknown, version: number): ExportedEvent | null {
  if (!isObject(raw) || typeof raw.kind !== 'string' || !KINDS.has(raw.kind) || !finite(raw.t)) return null;
  const offsetS = finite(raw.offsetS) ? raw.offsetS : undefined;
  // Before schema 3 a tap's time was the raw heard time: take off the offset it was made with, as it is stored now.
  const t = version < 3 && (raw.kind === 'one' || raw.kind === 'reanchor') && offsetS !== undefined ? raw.t - offsetS : raw.t;
  let event: CountEvent;
  switch (raw.kind) {
    case 'shift':
      if (raw.delta !== 1 && raw.delta !== -1) return null;
      event = { kind: 'shift', t, delta: raw.delta };
      break;
    case 'clave':
      if (typeof raw.direction !== 'string' || !CLAVE.has(raw.direction)) return null;
      event = { kind: 'clave', t, direction: raw.direction as '3-2' | '2-3' | 'unknown' };
      break;
    case 'drop':
      if (typeof raw.tap !== 'string' || raw.tap === '') return null;
      event = { kind: 'drop', t, tap: raw.tap };
      break;
    case 'auto': {
      const proposal = readProposal(raw.proposal);
      if (!proposal) return null;
      event = { kind: 'auto', t, proposal };
      break;
    }
    case 'level':
      if (raw.change !== 'halve' && raw.change !== 'double') return null;
      event = { kind: 'level', t, change: raw.change };
      break;
    case 'undo':
      if (raw.tap !== undefined && (typeof raw.tap !== 'string' || raw.tap === '')) return null;
      event = typeof raw.tap === 'string' ? { kind: 'undo', t, tap: raw.tap } : { kind: 'undo', t };
      break;
    default:
      event = { kind: raw.kind as 'one' | 'reanchor' | 'flip' | 'half' | 'reset' | 'confirm', t };
  }
  return {
    event,
    ...(finite(raw.wall) ? { wall: raw.wall } : {}),
    ...(offsetS !== undefined ? { offsetS } : {}),
    ...(typeof raw.device === 'string' ? { device: raw.device } : {}),
  };
}

/** Read a file's text. Schemas 1 to 5 are read; anything else is refused with a sentence. */
export function parseExport(text: string): ParseResult {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return { ok: false, sentence: 'That file could not be read. Choose a file made with Export count.' };
  }
  if (!isObject(doc) || !Array.isArray(doc.events)) return { ok: false, sentence: NOT_A_COUNT };
  const version = doc.version === undefined ? 1 : doc.version;
  if (!finite(version) || version < 1 || !Number.isInteger(version)) return { ok: false, sentence: NOT_A_COUNT };
  if (version > SCHEMA_VERSION) {
    return { ok: false, sentence: 'That count was saved by a newer version of salsacount. Reload the app to get the new version, then try again.' };
  }
  const events: ExportedEvent[] = [];
  for (const raw of doc.events) {
    const e = readEvent(raw, version);
    if (!e) return { ok: false, sentence: NOT_A_COUNT }; // one bad entry: nothing is imported, so nothing is half-added
    events.push(e);
  }
  const track = isObject(doc.track) ? doc.track : {};
  const cal = isObject(doc.calibration) && finite(doc.calibration.offsetS) && typeof doc.calibration.device === 'string'
    ? {
      offsetS: doc.calibration.offsetS,
      device: doc.calibration.device,
      ...(finite(doc.calibration.taps) ? { taps: doc.calibration.taps } : {}),
      ...(typeof doc.calibration.madeAt === 'string' ? { madeAt: doc.calibration.madeAt } : {}),
    }
    : null;
  return {
    ok: true,
    doc: {
      version,
      trackId: typeof track.sha256 === 'string' && track.sha256 ? track.sha256 : null,
      name: typeof track.name === 'string' ? track.name : null,
      events,
      calibration: cal,
      trackerVersion: isObject(doc.tracker) && typeof doc.tracker.version === 'string' ? doc.tracker.version : null,
    },
  };
}

/** What two events have to share to be the same event. Times are the exact numbers written by the same engine. */
function identity(e: CountEvent): string {
  const extra = e.kind === 'auto' ? `${e.proposal.bpm}/${e.proposal.one}` : e.kind === 'level' ? e.change : '';
  return `${e.kind}|${e.t}|${e.kind === 'shift' ? e.delta : ''}|${e.kind === 'clave' ? e.direction : ''}|${e.kind === 'drop' || e.kind === 'undo' ? (e.tap ?? '') : ''}|${extra}`;
}

export interface MergePlan {
  /** The events to append, in the file's order. */
  readonly add: readonly CountEvent[];
  readonly walls: readonly (number | undefined)[];
  /** When, with which offset and on which device each added event was made (same order as `add`). */
  readonly meta: readonly { readonly wall?: number | undefined; readonly offsetS?: number | undefined; readonly device?: string | undefined }[];
  /** File events already in this device's log (not added again). */
  readonly alreadyHere: number;
  /** File `undo` events not added because the event they cancelled was already here (this device's log keeps its own history of it). */
  readonly undosSkipped: number;
}

/**
 * The events of a file this device does not have. Each event already in `existing` matches one file event at most (a file with
 * two identical taps against a log with one adds one). An `undo` is added only when the event it cancels in the file's own history
 * was added too, so it cannot cancel the wrong event here.
 */
export function mergeEvents(existing: readonly CountEvent[], incoming: readonly ExportedEvent[]): MergePlan {
  const have = new Map<string, number>();
  for (const e of existing) have.set(identity(e), (have.get(identity(e)) ?? 0) + 1);
  const add: CountEvent[] = [];
  const walls: (number | undefined)[] = [];
  const meta: { wall?: number | undefined; offsetS?: number | undefined; device?: string | undefined }[] = [];
  const stack: { readonly event: CountEvent; readonly added: boolean }[] = []; // for the file's own history: each event in force, and was it added?
  let alreadyHere = 0;
  let undosSkipped = 0;
  for (const item of incoming) {
    const e = item.event;
    if (e.kind === 'undo') {
      let i = stack.length - 1; // a plain undo cancels the last; one that names a tap cancels the latest drop of that tap
      if (e.tap !== undefined) while (i >= 0 && !(stack[i]!.event.kind === 'drop' && (stack[i]!.event as { tap: string }).tap === e.tap)) i--;
      const target = i >= 0 ? stack.splice(i, 1)[0] : undefined;
      if (target?.added) { add.push(e); walls.push(item.wall); meta.push({ wall: item.wall, offsetS: item.offsetS, device: item.device }); } else undosSkipped++;
      continue;
    }
    const key = identity(e);
    const n = have.get(key) ?? 0;
    if (n > 0) {
      have.set(key, n - 1);
      alreadyHere++;
      stack.push({ event: e, added: false });
    } else {
      add.push(e);
      walls.push(item.wall);
      meta.push({ wall: item.wall, offsetS: item.offsetS, device: item.device });
      stack.push({ event: e, added: true });
    }
  }
  return { add, walls, meta, alreadyHere, undosSkipped };
}

export type ImportPlan =
  | { readonly kind: 'refused'; readonly sentence: string }
  | { readonly kind: 'merge'; readonly plan: MergePlan; readonly sentence: string };

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** Decide what importing `doc` into the open song (`trackId`, with `existing` events) does, and say it in a sentence. */
export function planImport(trackId: string, existing: readonly CountEvent[], doc: ParsedExport): ImportPlan {
  if (doc.trackId && doc.trackId !== trackId) {
    return { kind: 'refused', sentence: `That count was made for a different song${doc.name ? ` (${doc.name})` : ''}. Open that song and import it there.` };
  }
  const plan = mergeEvents(existing, doc.events);
  const parts: string[] = [];
  parts.push(plan.add.length === 0 ? 'Nothing new in that file' : `Added ${plural(plan.add.length, 'change', 'changes')} from the file`);
  if (plan.alreadyHere > 0) parts.push(`${plan.alreadyHere} already here`);
  if (plan.undosSkipped > 0) parts.push(`${plural(plan.undosSkipped, 'undo', 'undos')} of changes already here left out`);
  const unchecked = doc.trackId ? '' : ' The file did not say which song it was made for, so it was added to this one.';
  return { kind: 'merge', plan, sentence: `${parts.join('; ')}.${unchecked}` };
}

// ── Export all my counts (S9e) ──

/** The schema of the "Export all my counts" file. The song entries' events are in the event schema of `SCHEMA_VERSION`, given beside it. */
export const ALL_COUNTS_SCHEMA = 1;

export interface AllCountsSong {
  /** SHA-256 of the file's bytes (the song's identity), name and duration as the device's library has them; null when the library no longer lists the song (its taps stayed on the device). */
  readonly sha256: string;
  readonly name: string | null;
  readonly duration: number | null;
  /** Flat, as in a song's count file: each entry is the event's own fields plus `wall`, `offsetS` and `device`. */
  readonly events: readonly Record<string, unknown>[];
}

export interface AllCounts {
  readonly app: 'salsacount';
  readonly kind: 'all-counts';
  readonly schema: number;
  /** The event schema of the songs' logs (`SCHEMA_VERSION`). */
  readonly eventSchema: number;
  readonly exportedAt: string;
  readonly songs: readonly AllCountsSong[];
}

export interface AllCountsInput {
  readonly songs: readonly {
    readonly id: string;
    readonly name: string | null;
    readonly duration: number | null;
    readonly records: readonly { readonly event: CountEvent; readonly wall: number; readonly offsetS: number; readonly device?: string }[];
  }[];
  readonly now?: Date;
}

/** Every stored song's identity and event log in one document, no audio, songs in the order given (the app gives the library's, most recent first, then any that only have a log). */
export function buildAllExport(input: AllCountsInput): AllCounts {
  return {
    app: 'salsacount',
    kind: 'all-counts',
    schema: ALL_COUNTS_SCHEMA,
    eventSchema: SCHEMA_VERSION,
    exportedAt: (input.now ?? new Date()).toISOString(),
    songs: input.songs.map((s) => ({
      sha256: s.id, name: s.name, duration: s.duration,
      events: s.records.map((r) => ({ ...r.event, wall: r.wall, offsetS: r.offsetS, ...(r.device !== undefined ? { device: r.device } : {}) })),
    })),
  };
}
